import React, {useState, useRef, useEffect} from "react";
import {jwtDecode} from "jwt-decode";
import {useAppContext} from "./context/AppContext";
import ChatHistory from "./components/ChatHistory";
import ChatInput from "./components/ChatInput";
import Footer from "./components/Footer";
import SiderBar from "./components/SiderBar";
import {Message, JWTUserProps} from "./types/types";
import LoginFormContainer from "./components/LoginFormContainer";
import Spinner from "./components/Spinner";

const WEBSOCKET_URL =
  "wss://iris-bedrock-backend-dev.icxeed.ai/chatbot/ws/chat/";

const App = () => {
  const {isLoggedIn} = useAppContext();
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [hasStartedChat, setHasStartedChat] = useState(false);
  const [user, setUser] = useState<JWTUserProps | null>(null);

  useEffect(() => {
    const token = localStorage.getItem("iris-accessToken");
    if (!token) {
      setUser(null);
      return;
    }

    try {
      const decoded = jwtDecode<JWTUserProps>(token);
      setUser(decoded);
    } catch (err) {
      setUser(null);
    }
  }, [isLoggedIn]);

  const bottomRef = useRef<HTMLDivElement>(null);
  const socketRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    if (!isLoggedIn) return;

    let reconnectAttempts = 0;
    let socket: WebSocket | null = null;
    let reconnectTimeout: ReturnType<typeof setTimeout>;

    const connectSocket = () => {
      if (socket?.readyState === WebSocket.OPEN) return;
      socket = new WebSocket(WEBSOCKET_URL);
      socketRef.current = socket;

      socket.onopen = () => {
        setIsConnected(true);
        reconnectAttempts = 0;
      };

      socket.onclose = () => {
        setIsConnected(false);

        if (!document.hidden) {
          reconnectAttempts++;
          const delay = Math.min(5000, reconnectAttempts * 1000);
          reconnectTimeout = setTimeout(connectSocket, delay);
        }
      };

      socket.onerror = () => {
        socket?.close();
      };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data?.thinking) {
            setIsThinking(true);
            return;
          }

          const sender = data?.botResponse?.role;
          const rawMessage = data?.botResponse?.content[0]?.text || "";
          const imageObject: Record<string, string> =
            data?.images && typeof data.images === "object" ? data.images : {};

          const formattedMessage = rawMessage
            .replace(
              /<url=([^>]+)>(.*?)<\/url>/g,
              (_: any, url: any, text: any) =>
                `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color:#01C2F3;">${text}</a>`,
            )
            .replace(/\[([^\]]+)\]/g, (match: any, filename: any) => {
              const url = imageObject[filename];
              return url
                ? `<img src="${url}" alt="${filename}" style="max-width:100%; margin:10px 0; display:block;" />`
                : match;
            });

          const botMessage: Message = {
            sender,
            message: formattedMessage,
            imageArray: Object.values(imageObject),
          };

          setMessages((prev) => [...prev, botMessage]);
          setIsLoading(false);
          setIsThinking(false);
        } catch (err) {
          setIsLoading(false);
          setIsThinking(false);
        }
      };
    };

    connectSocket();

    const handleVisibilityChange = () => {
      if (
        !document.hidden &&
        (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN)
      ) {
        connectSocket();
      }
    };

    // 👇 Also reconnect on window focus
    const handleFocus = () => {
      if (
        !socketRef.current ||
        socketRef.current.readyState !== WebSocket.OPEN
      ) {
        connectSocket();
      }
    };

    const handleBeforeUnload = () => {
      socket?.close();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      clearTimeout(reconnectTimeout);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
      socket?.close();
    };
  }, [isLoggedIn]);

  useEffect(() => {
    function updateVH() {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    }

    updateVH();

    window.addEventListener("resize", updateVH);
    window.addEventListener("orientationchange", updateVH);

    return () => {
      window.removeEventListener("resize", updateVH);
      window.removeEventListener("orientationchange", updateVH);
    };
  }, []);

  const onSubmitHandler = (message?: string) => {
    const val =
      typeof message === "string" ? message.trim() : inputValue.trim();

    if (!val || !socketRef.current) return;

    const userMessage: Message = {
      sender: "user",
      message: val,
      accountId: user?.id,
      email: user?.email,
      userType: user?.userType,
      companyName: user?.companyName,
      firstName: user?.firstName,
      lastName: user?.lastName,
    };

    setMessages((prev) => [...prev, userMessage]);
    setHasStartedChat(true);
    setIsLoading(true);

    socketRef.current.send(JSON.stringify(userMessage));

    setInputValue("");
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({behavior: "smooth"});
  }, [messages]);

  return (
    <div className="root-container-blk">
      {!isLoggedIn && <LoginFormContainer />}
      {!isLoggedIn ? (
        <>
          <SiderBar isExpanded={isExpanded} setIsExpanded={setIsExpanded} />
          <div className="iris-container"></div>
        </>
      ) : (
        <React.Fragment>
          <SiderBar isExpanded={isExpanded} setIsExpanded={setIsExpanded} />
          {!isConnected ? (
            <div className="iris-container" style={{justifyContent: "center"}}>
              <Spinner variant="large-spinner" alignSpin="center-spinner" />
            </div>
          ) : (
            <div className="iris-container">
              <div
                className={`iris-center-content ${
                  hasStartedChat ? "messageNotEmpty" : "messageEmpty"
                }`}>
                <h1
                  className={`iris-title ${hasStartedChat ? "fade-out" : ""} ${
                    isExpanded ? "sidebar-expanded" : ""
                  }`}>
                  iRis - your agentic Amazon Connect support partner.
                </h1>
                <ChatHistory
                  messages={messages}
                  bottomRef={bottomRef}
                  isLoading={isLoading}
                  isThinking={isThinking}
                  setInputValue={setInputValue}
                  onSubmit={onSubmitHandler}
                />
              </div>
              <ChatInput
                messages={messages}
                inputValue={inputValue}
                setInputValue={setInputValue}
                onSubmit={onSubmitHandler}
                isExpanded={isExpanded}
                isDisabled={isLoading || isThinking}
                hasStartedChat={hasStartedChat}
              />
              <Footer />
              <div className="overflow-div-blk"></div>
            </div>
          )}
        </React.Fragment>
      )}
    </div>
  );
};

export default App;
